/** Arranging the selection: plain groups, lining up, spreading out, front and back. */
import { toast } from "../../shared/toast";
import type { BoardCtx } from "./context";
import { align, bottomZ, distribute, topZ } from "./geometry";
import type { BoardPart } from "./types";

/** The arranging members of the board service. */
export function arrangePart(ctx: BoardCtx): BoardPart<"group" | "ungroup" | "align" | "distribute" | "order"> {
  const { view, actions } = ctx.deps;
  return {
    async group() {
      const ids = ctx.selectedNodes();
      if (ids.length < 2) return void toast("Select two items or more to group them.");
      const shared = ctx.nodeOf(ids[0]!)?.group ?? null;
      const parent = ids.every((id) => (ctx.nodeOf(id)?.group ?? null) === shared) ? shared : null;
      const id = `group-${Math.random().toString(36).slice(2, 8)}`;
      if (await ctx.send([{ edit: "add_group", id, name: "Group", parent, members: ids, note: "" }])) toast("Grouped: they move together. Ctrl+Shift+G takes them apart.");
    },
    async ungroup() {
      const s = view.state.selection;
      const id = s?.type === "group" && !ctx.isFrame(s.id) ? s.id : ctx.plainGroupOf(ctx.selectedNodes());
      if (!id) return void toast("Select a group to take it apart.");
      await actions.removeGroup(id);
    },
    async align(how) {
      const places = ctx.startPlaces(ctx.selectedNodes());
      if (places.size < 2) return void toast("Select two items or more to line them up.");
      await ctx.send(ctx.placeEdits(align(places, how)));
    },
    async distribute(axis) {
      const places = ctx.startPlaces(ctx.selectedNodes());
      if (places.size < 3) return void toast("Select three items or more to spread them out.");
      await ctx.send(ctx.placeEdits(distribute(places, axis)));
    },
    async order(where) {
      const ids = ctx.selectedNodes();
      if (!ids.length) return;
      const places = ctx.startPlaces(ids);
      const sorted = [...places].sort(([, a], [, b]) => (a.z ?? 0) - (b.z ?? 0));
      const from = where === "front" ? topZ(ctx.content()) + 1 : bottomZ(ctx.content()) - sorted.length;
      await ctx.send(ctx.placeEdits(new Map(sorted.map(([id, p], i) => [id, { ...p, z: from + i }]))));
    },
  };
}
